import {
  getDefaultTimezone,
  getRequiresActivationWithLimitsInitialValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';
import { OfferFragment } from '@occc/graphql';
import { AwardType } from '@occc-app/offerHelpers/types';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { getAwardCountInitialValues, LimitTimePeriod } from '@occc/feat-limits';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import {
  LoyaltyEventConfigureFormInitialValues,
  LoyaltyEventEarningMechanismInitialValues,
} from '../../../types';

const transformOfferEarningMechanism = (
  earningMechanism: OfferFragment['earningMechanism'],
): LoyaltyEventEarningMechanismInitialValues => {
  if (!earningMechanism || !earningMechanismHelper.isLoyaltyEvent(earningMechanism)) {
    return {};
  }

  const award = earningMechanism.loyaltyEventAward;
  const loyaltyEvent = {
    eventType: earningMechanism.loyaltyEventEventType,
    id: earningMechanism.id,
  };

  if (award && awardHelper.isPointsAward(award)) {
    return {
      loyaltyEvent,
      awardType: AwardType.AwardPoints,
      awardPoints: { points: award.points },
    };
  }

  if (award && awardHelper.isVouchersAward(award)) {
    return {
      loyaltyEvent,
      awardType: AwardType.AwardVouchers,
      awardVouchers:
        typeof award.voucherExpirationDays === 'number'
          ? {
              voucheredOffer: award.voucheredOffer,
              voucherCount: award.voucherCount,
              voucherExpirationDays: award.voucherExpirationDays,
              isEnabled: true,
            }
          : {
              voucheredOffer: award.voucheredOffer,
              voucherCount: award.voucherCount,
              isEnabled: false,
            },
    };
  }

  return { loyaltyEvent, awardType: AwardType.AwardNullable };
};

const getOfferConfigureFormInitialValues = (): LoyaltyEventConfigureFormInitialValues => ({
  earningMechanism: {},
  awardVouchers: {
    isEnabled: false,
    voucherCount: 1,
  },
  awardType: AwardType.AwardNullable,
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
  },
  loyaltyEvent: {
    eventType: '',
  },
  activation: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    requiresActivation: {
      isMustFulfillWithinEnabled: false,
      allowMultipleFulfillmentsOnActivation: false,
    },
  },
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
  visibleForCommandCenterFulfillment: false,
});

export const transformOfferToInitialValues = (
  offer?: OfferFragment,
): LoyaltyEventConfigureFormInitialValues => {
  const values = getOfferConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    ...transformOfferEarningMechanism(offer.earningMechanism),
    activation: getRequiresActivationWithLimitsInitialValues(offer, values.activation),
    awardCount: getAwardCountInitialValues(offer, values.awardCount) as NonNullable<
      LoyaltyEventConfigureFormInitialValues['awardCount']
    >,
    ...transformOfferToFulfillmentWindowFormValue(offer),
    customData: transformCustomData(offer.customData),
    visibleForCommandCenterFulfillment: !!offer.visibleForCommandCenterFulfillment,
  };
};
